

































































import Vue, { PropType, VueConstructor } from "vue";
import { License, LicenseData } from "@/models/license";
import { DeviceConfiguration } from "@/models/deviceConfiguration";
import validationMixin from "@/components/mixins/validationMixin";
import VaButton from "@/components/atoms/VaButton.vue";
import LicenseService from "@/services/licenseService";
import onPremService from "@/services/onPremService";
import { translateOr } from "@/services/externalProvidersService";

export default (
  Vue as VueConstructor<Vue & InstanceType<typeof validationMixin>>
).extend({
  name: "NewLicense",
  mixins: [validationMixin],
  components: {
    VaButton,
  },
  props: {
    // The device being activated: its tenant code decides which licenses it can take.
    deviceConfiguration: {
      type: Object as PropType<DeviceConfiguration>,
      required: false,
    },
  },
  async beforeMount() {
    this.$store.commit("license/resetCurrentLicense");
  },
  data() {
    return {
      newLicense: {
        licenseKey: "",
        isRenewal: false,
      } as LicenseData,
      fileLicense: undefined as File | undefined,
      incompatibleKey: "" as string,
    };
  },
  methods: {
    /**
     * A license lists the device tenant codes it supports; the device says its own in the
     * wizard configuration. A license that declares none is refused (card #14590). A device
     * build that does not say its tenant code cannot be checked, as for the existing-license
     * list, which is not filtered for it either.
     */
    isCompatible(license: License): boolean {
      if (!license.tenantCodes?.length) return false;
      const tenantCode = this.deviceConfiguration?.tenantCode;
      if (!tenantCode) return true;
      return license.tenantCodes.includes(tenantCode);
    },
    onDismiss(): void {
      this.$emit("onDismiss");
    },
    onFinish(): void {
      this.$emit("onFinish", this.newLicense);
    },
    async onUpload(): Promise<void> {
      if (this.fileLicense) {
        const r = (await onPremService.uploadLicense(this.fileLicense)) as any;
        const res = await LicenseService.getLicense(r.data as string);

        const licenseDetail = {
          key: res.key,
          licensePackageId: res.id,
          name: res.name,
          sku: res.sku,
          status: res.status,
          creationDate: res.creationDate,
          expirationDate: res.expirationDate,
          isRoom: res.isPod,
          activations: res.activations,
          users: res.detailUsers,
          isPod: res.isPod,
        };
        this.$emit("onFinish", licenseDetail);
        this.fileLicense = undefined;
      }
    },

    async onSave(): Promise<void> {
      const form = this.$refs.newLicenseForm as Vue & {
        validate: () => boolean;
        resetValidation: () => void;
      };
      if (form.validate()) {
        // The key is added to the console regardless of the tenant check below.
        const addlicense = await this.$store.dispatch("license/addLicense", {
          data: this.newLicense,
          refreshList: false,
        });
        if (addlicense) {
          const res = await LicenseService.getLicense(
            this.newLicense.licenseKey
          );
          if (!this.isCompatible(res)) {
            // Dedicated message; the form stays for another key, "Back" offers the existing ones.
            this.incompatibleKey = res.key || this.newLicense.licenseKey;
            this.newLicense.licenseKey = "";
            form.resetValidation();
            return;
          }
          this.incompatibleKey = "";
          const licenseDetail = {
            key: res.key,
            licensePackageId: res.licensePackageId,
            name: res.name,
            sku: res.sku,
            status: res.status,
            creationDate: res.creationDate,
            expirationDate: res.expirationDate,
            isRoom: res.isPod,
            activations: res.activations,
            users: res.detailUsers,
            isPod: res.isPod,
          };
          this.$emit("onFinish", licenseDetail);
        }
      }
    },
  },
  computed: {
    isOnPrem(): boolean {
      return this.$store.getters["onprem/isOnPrem"];
    },
    notCompatibleMessage(): string {
      return translateOr(
        "roomWizard.errors.licenseNotCompatible",
        "This license does not support the device being activated. Enter a different license key, or go back and select one of the existing licenses."
      );
    },
  },
});
